import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { withRouter, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import qs from 'query-string';

import { AlbumForWebGalleries } from '../../graphql/queries/album';
import { AlbumFolderForWebGalleries } from '../../graphql/queries/album-folder';
import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import NotFound from '../../../pro/views/NotFound';
import { isAlbumFolder } from '../../../../util/album-folder';
import {
  getWebGalleryEntityPath,
  requiresAuthentication,
  webGalleryIsInviteOnly,
} from '../../utilities/web-galleries';
import ErrorView from '../../../../components/global/ErrorView';
import { openModal as openModalAction } from '../../../../actions/modal-actions';
import { WEB_GALLERY_PRO_ONBOARDING_MODAL } from '../WebGalleryProOnboardingInterstitial';
import { ALL_PHOTOS_MODE } from '../../../../constants/album-folder';
import { ALBUM_SUBTYPE } from '../../../../constants/albums';

const propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
  }),
  loading: PropTypes.bool,
  openModal: PropTypes.func.isRequired,
  authenticated: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
};

const WebGalleryOnboardingRedirect = ({
  entity,
  loading,
  openModal,
  authenticated,
  location,
}) => {
  const albumFolder = isAlbumFolder(entity)
    ? entity
    : entity?.root_album_folder;

  const isOnboardable = entity?.joins_enabled || albumFolder?.joins_enabled;
  const isWebGallery = entity?.is_web_gallery;
  const isSchoolSubtype = albumFolder?.album_subtype === ALBUM_SUBTYPE.SCHOOLS;
  const isCampSubtype = albumFolder?.album_subtype === ALBUM_SUBTYPE.CAMPS;

  const isPrivateFaceblocked =
    !albumFolder?.joins_enabled &&
    albumFolder?.all_photos_mode === ALL_PHOTOS_MODE.PRIVATE;

  const isPublicWithJoinsEnabled =
    isOnboardable && albumFolder?.all_photos_mode === ALL_PHOTOS_MODE.PUBLIC;

  useEffect(() => {
    if (
      albumFolder &&
      isOnboardable &&
      isWebGallery &&
      !isSchoolSubtype &&
      !isCampSubtype
    ) {
      // Open up R/L door:
      openModal(WEB_GALLERY_PRO_ONBOARDING_MODAL, {
        albumFolderId: albumFolder.id,
      });
    }
  }, [albumFolder, isOnboardable, isWebGallery]);

  if (!loading && !entity) {
    return <NotFound />;
  }

  if (loading) {
    return <FullScreenLoader />;
  }

  if (requiresAuthentication(entity, authenticated)) {
    // If requires auth and onboardable user can just auth during onboarding.
    if (isOnboardable) {
      // Preserve any query parameters, especially fromGallery=true
      const queryParams = location?.search ? `${location.search}` : '';
      return (
        <Redirect
          to={`/discover/album-folder/${albumFolder.id}${queryParams}`}
        />
      );
    }

    return (
      <Redirect
        to={{
          pathname: '/auth/phone',
          search: `?${qs.stringify({
            next: `${location.pathname}${location.search || ''}`,
          })}`,
        }}
      />
    );
  }

  if (isOnboardable) {
    // Preserve any query parameters, especially fromGallery=true
    const queryParams = location?.search ? `${location.search}` : '';
    return (
      <Redirect to={`/discover/album-folder/${albumFolder.id}${queryParams}`} />
    );
  }

  if (isWebGallery && !webGalleryIsInviteOnly(entity)) {
    return <Redirect to={getWebGalleryEntityPath(entity)} />;
  }

  if (isPrivateFaceblocked || isPublicWithJoinsEnabled) {
    return <Redirect to={`/s/all-photos-access/${albumFolder.id}`} />;
  }

  // Safeguard against non-onboardable and non-wg entities:
  return (
    <ErrorView
      location={{
        state: {
          error:
            "It looks like something went wrong with the page you're trying to access. Please contact one of our administrators.",
        },
      }}
    />
  );
};

WebGalleryOnboardingRedirect.propTypes = propTypes;

const withAlbumForWebGalleries = graphql(AlbumForWebGalleries, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
    // In order for openModal to work properly,
    // ssr needs to be disabled.
    ssr: false,
  }),
  props: ({ data: { loading, album } }) => ({
    loading,
    entity: album,
  }),
  skip: ({ match }) => !match.params.albumId,
});

const withAlbumFolderForWebGalleries = graphql(AlbumFolderForWebGalleries, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
    // In order for openModal to work properly,
    // ssr needs to be disabled.
    ssr: false,
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    entity: albumFolder,
    loading,
  }),
  skip: ({ match }) => !match.params.albumFolderId,
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const mapDispatchToProps = {
  openModal: openModalAction,
};

export { WebGalleryOnboardingRedirect as WebGalleryOnboardingRedirectComponent };

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withAlbumForWebGalleries,
  withAlbumFolderForWebGalleries,
)(WebGalleryOnboardingRedirect);
